import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useEffect } from 'react';
import { showResult } from 'infrastructure/hooks/utils';
import {
  type CcmStatuses,
  type ReadingStatus,
  type ReadingType,
  type RpmStatuses,
} from 'infrastructure/enums';
import {
  type TStatuses,
  type Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import { type IPatientThresholds } from 'infrastructure/interfaces';
import { apiPatientReadingsNew } from 'services/dataService/resources';

export interface IPatientReadingsParams {
  page: number;
  items: number;
  startTime: number;
  endTime: number;
  status?: ReadingStatus;
  searchTerm?: string;
  applySpecialFilter?: boolean;
  assignee?: string;
  reverse?: boolean;
  reviewed?: boolean;
}

export interface IPatientReading {
  id: string;
  dateTime: number;
  deviceId: string;
  readingType: ReadingType;
  value: Value;
  createdAt: number;
  statuses: TStatuses;
  globalStatus: ReadingStatus;
  doctor: {
    title: string;
    firstName: string;
    lastName: string;
  };
}

export interface IPatientDataItem {
  patient: {
    guid: string;
    firstName: string;
    lastName: string;
    birthDate: string;
    threshold: IPatientThresholds;
    language: string;
    status: RpmStatuses;
    ccmStatus: CcmStatuses;
  };
  readings?: IPatientReading;
}

export interface IPatientReadingsResponse {
  patientsCount: number;
  data: IPatientDataItem[];
}

const fetchPatientReadings = async (
  params: IPatientReadingsParams,
): Promise<{ patientReadings: IPatientDataItem[]; count: number }> => {
  const endpoint = apiPatientReadingsNew(params);
  const { data, error } = await dataService.getList<IPatientReadingsResponse>(
    endpoint,
  );

  if (error) {
    throw new Error(error);
  }

  if (!data) {
    throw new Error('No data returned from API');
  }

  const patientReadings = data.data.map((item) => ({
    patient: item.patient,
    readings: item.readings,
  }));

  return {
    patientReadings,
    count: data.patientsCount,
  };
};

export const usePatientReadings = (params: IPatientReadingsParams) => {
  const queryKey = [
    'home-dashboard',
    'patient-readings',
    params.page,
    params.items,
    params.startTime,
    params.endTime,
    params.status,
    params.searchTerm,
  ];

  const { data, isLoading, isFetching, error, refetch } = useQuery<
    { patientReadings: IPatientDataItem[]; count: number },
    Error
  >({
    queryKey,
    queryFn: () => fetchPatientReadings(params),
    keepPreviousData: true,
  });

  useEffect(() => {
    if (error) {
      showResult(error.message);
    }
  }, [error]);

  return {
    patientReadings: data?.patientReadings ?? [],
    count: data?.count ?? 0,
    isLoading,
    isFetching,
    refetch,
  };
};
